'use strict';
// THE NAMES CALIBER KEEPS (owner call 2026-10-04: "drop the @ from the url …
// make any name that collides with a /page just unavailable"). A person's
// pages live at /<username>/…, flush, so the first segment of an address is
// either one of Caliber's own — a page, a public route, a server path, a file
// — or somebody's username. This is the one list of Caliber's own: the
// server (server.js, lib/public-address) and the browser (frontend
// lib/accountBase, through vite's cjs-engines wrapper) read it. ofSelf does
// NOT keep these words out of the usernames it gives (validate_username has
// no list): a holder of one has no address here (isAddressable).
//
// test/reserved-paths.test.js fails when a top-level route in App.tsx or
// server.js, or a file the build serves at the root, is missing from it, so
// a page cannot ship onto a name a person might hold.

/** The app's own pages, current and legacy (frontend/src/App.tsx). */
const APP = [
  'apply', 'artifacts', 'as', 'aspire', 'assessment-lab', 'assessments', 'capture', 'cards', 'community', 'connect',
  'dev-onboarding', 'documents', 'experience', 'global-skills-community', 'help', 'home', 'hustle',
  'identity', 'inbox', 'initiate-document', 'initiate-identity', 'initiate-quiz', 'interview', 'jobs', 'library', 'skill-library', 'lab',
  'my-personality-card', 'my-resume', 'my-skills-card', 'network', 'onboarding', 'overview', 'personality', 'profile-settings',
  'preview', 'projects', 'proof', 'prove', 'public', 'qualifications', 'references', 'resume', 'resumes', 'roles',
  'search', 'settings', 'showcase', 'skills', 'skills-card', 'sources', 'step-2-role', 'step-3-skills',
  'share', 'step-3b-resume', 'step-4-career', 'tools', 'tracks', 'not-on-a-card',
  // the card world (docs/CARD_WORLD.md): home at /world, the atrium and its rooms
  'rooms', 'world',
];

/** The site's public pages: sign-in, a shared card or profile, a referee's forms, the long reads. */
const PUBLIC = ['attest', 'auth', 'c', 'callback', 'callback.html', 'docs', 'enter', 'p', 'philosophy', 'recruiters', 'roadmap', 'step-1-register', 'v', 'verify', 'vouch', 'welcome'];

/** What the server answers before the app: its API, its health, the share images. */
const SERVER = ['api', 'health', 'og'];

/** What the build serves at the root (frontend/public, vite's assets, the repo's static roots). */
const FILES = [
  'apple-touch-icon.png', 'assets', 'cosm', 'cosm-svgs', 'docs', 'epubs', 'favicon.ico', 'favicon.svg',
  'index.html', 'manifest.json', 'og.png', 'robots.txt', 'sitemap.xml', 'star-12.svg', '.well-known',
];

/** Names a site keeps for itself whether or not a page stands there yet. */
const KEPT = [
  'about', 'account', 'admin', 'app', 'blog', 'contact', 'explore', 'login', 'logout', 'new', 'privacy',
  'signin', 'signout', 'signup', 'support', 'terms', 'u', 'user', 'users', 'www',
];

const RESERVED = Object.freeze([...new Set([...APP, ...PUBLIC, ...SERVER, ...FILES, ...KEPT].map((w) => w.toLowerCase()))].sort());
const SET = new Set(RESERVED);

/** Names that would read as Caliber itself, a platform surface, or nobody —
 *  never an address, whoever holds them on ofSelf (the impersonation set the
 *  handle rules kept off /@handle until 2026-10-04). */
const IMPERSONATION = new Set([
  'caliber', 'ofself', 'admin', 'administrator', 'root', 'system', 'official', 'staff', 'team',
  'support', 'help', 'security', 'abuse', 'moderator', 'mod', 'legal', 'privacy', 'terms', 'contact',
  'api', 'app', 'www', 'mail', 'email', 'login', 'logout', 'signin', 'signup', 'register', 'callback',
  'account', 'accounts', 'settings', 'profile', 'profiles', 'public', 'private', 'verify', 'verified',
  'record', 'records', 'card', 'cards', 'skills', 'apply', 'hustle', 'lab', 'identity', 'home', 'welcome',
  'onboarding', 'assets', 'static', 'health', 'robots', 'favicon',
  'me', 'you', 'user', 'users', 'anonymous', 'null', 'undefined', 'unknown', 'test', 'demo', 'sample', 'example',
]);
/** caliber-…, ofself_… and the like read as the product too. */
const BRAND = /^(caliber|ofself)([-_.]|$)/;
/** What an ofSelf username may be in an address: the platform's shape, up to 64. */
const USERNAME_SHAPE = /^[A-Za-z0-9._-]{1,64}$/;

/** Is this first segment Caliber's own (never a username)? Case-insensitive. */
function isReserved(segment) {
  return SET.has(String(segment == null ? '' : segment).toLowerCase());
}

/** The first segment of a path: '/dark/skills' → 'dark'; '/' → ''. Decoded. */
function firstSegment(pathname) {
  const seg = String(pathname || '').split('/')[1] || '';
  try { return decodeURIComponent(seg); } catch { return seg; }
}

/** AN OLD /@<username>/… LINK (owner call 2026-10-04: the @ is dropped): the
 *  same address flush — or null when what follows the @ is not a username.
 *  Until 2026-10-06 the @ was simply cut off, so /@/evil.com became
 *  //evil.com: a redirect to another site. The server (server.js) and the
 *  browser (lib/accountBase, App's AddressOrHome) both ask this. */
const OLD_ADDRESS = /^\/@([A-Za-z0-9._-]{1,64})(\/.*)?$/;
function fromOldAddress(pathname) {
  const m = OLD_ADDRESS.exec(String(pathname == null ? '' : pathname));
  return m ? `/${m[1]}${m[2] || ''}` : null;
}

/**
 * May this ofSelf username stand as a Caliber address — the base of its
 * holder's own pages and their public page at /<username>? Not when it is one
 * of Caliber's own first segments, the product's name, a platform surface or
 * nobody. ofSelf keeps none of them out of the usernames it gives (paradigm_sdk
 * validate_username has no list), so a holder of one has no address here: the
 * app runs at its bare addresses for them, and their sealed link still works.
 * The server also holds the moderation deny-list to it (lib/usernames).
 */
function isAddressable(username) {
  const s = String(username == null ? '' : username);
  if (!USERNAME_SHAPE.test(s)) return false;
  const lower = s.toLowerCase();
  return !SET.has(lower) && !IMPERSONATION.has(lower) && !BRAND.test(lower);
}

module.exports = { RESERVED, APP, PUBLIC, SERVER, FILES, KEPT, isReserved, firstSegment, fromOldAddress, isAddressable };
